import { cva, type VariantProps } from 'class-variance-authority';
import type { JSX } from 'react';
import * as React from 'react';

import { cn } from '~/lib/utils';

const typographyVariants = cva('', {
  variants: {
    variant: {
      // Figma text-5xl - 56px Bold
      '5xl': 'font-manrope font-bold text-5xl leading-[auto] text-white',

      // Figma text-4xl - 48px Bold
      '4xl': 'font-manrope font-bold text-4xl leading-[auto] text-white',

      // Figma text-3xl - 40px Bold
      '3xl': 'font-manrope font-bold text-3xl leading-[auto] text-white',

      // Figma text-2xl - 36px Bold/Regular
      '2xl': 'font-manrope font-bold text-2xl leading-[auto] text-white',
      '2xl-regular': 'font-manrope font-normal text-2xl leading-[auto] text-white',

      // Figma text-xl - 24px Bold
      xl: 'font-manrope font-bold text-xl leading-[auto] text-white',

      // Figma text-lg - 20px Bold
      lg: 'font-manrope font-bold text-lg leading-[auto] text-white',

      // Figma text-base - 16px Bold/Regular
      base: 'font-manrope font-normal text-base leading-[1.5] tracking-[0.16px] text-white',
      'base-bold': 'font-manrope font-bold text-base leading-[1.5] text-white',

      // Figma text-sm - 14px Bold/Regular
      sm: 'font-manrope font-normal text-sm leading-normal tracking-[0.14px] text-white',
      'sm-bold': 'font-manrope font-bold text-sm leading-[1.43] text-white',

      // Figma text-xs - 12px Bold/Regular
      xs: 'font-manrope font-normal text-xs leading-[auto] text-white',
      'xs-bold': 'font-manrope font-bold text-xs leading-[1.5] text-white',

      // Utility variants
      label: 'font-fragment-mono text-sm leading-normal tracking-[0.14px] uppercase text-white',
      'label-sm': 'font-fragment-mono text-xs leading-normal tracking-[0.12px] uppercase text-graphite-700',
      muted: 'font-fragment-mono text-xs leading-normal tracking-[0.12px] uppercase text-[#7b8d9e]',

      // Legacy aliases for backward compatibility
      hero: 'font-manrope font-bold text-5xl leading-[auto] text-white',
      h1: 'font-manrope font-bold text-4xl leading-[auto] tracking-[0.48px] text-white capitalize',
      h2: 'font-manrope font-normal text-2xl leading-[auto] text-white',
      'h2-medium': 'font-manrope font-bold text-2xl leading-[auto] text-white',
      body: 'font-manrope font-normal text-base leading-[1.5] tracking-[0.16px] text-white',
      'body-sm': 'font-manrope font-normal text-sm leading-normal tracking-[0.14px] text-white',
      'button-text': 'font-manrope font-bold text-[17px] leading-normal tracking-[0.17px] uppercase text-white text-center',
      link: 'font-manrope font-normal text-[15px] leading-normal text-white',
    },
  },
  defaultVariants: {
    variant: 'base',
  },
});

interface TypographyProps
  extends React.HTMLAttributes<HTMLElement>,
  VariantProps<typeof typographyVariants> {
  as?: keyof JSX.IntrinsicElements;
}

const Typography = ({
  className,
  variant,
  as,
  ...props
}: TypographyProps) => {
  const Comp = as || getDefaultElement(variant);

  return React.createElement(Comp, {
    className: cn(typographyVariants({ variant, className })),
    ...props,
  });
};

function getDefaultElement(
  variant: TypographyProps['variant']
): keyof JSX.IntrinsicElements {
  switch (variant) {
    case 'hero':
    case 'h1':
      return 'h1';
    case 'h2':
    case 'h2-medium':
      return 'h2';
    case 'label':
    case 'label-sm':
    case 'muted':
      return 'span';
    case 'link':
      return 'a';
    default:
      return 'p';
  }
}

export { Typography, typographyVariants };
